import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CartItem = ({ item, onUpdateQuantity, onRemove, onSaveForLater }) => {
  const handleQuantityChange = (newQuantity) => {
    if (newQuantity <= 0) {
      onRemove(item.id);
    } else if (newQuantity <= item.stock) {
      onUpdateQuantity(item.id, newQuantity);
    }
  };

  const isOutOfStock = item.stock === 0;
  const isLowStock = item.stock > 0 && item.stock <= 5;

  return (
    <div className="bg-surface border border-border rounded-card p-4 mb-4">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <div className="w-20 h-20 sm:w-24 sm:h-24 overflow-hidden rounded-input">
            <Image
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <div className="flex-1">
              <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-1">
                {item.name}
              </h3>
              <p className="text-text-secondary text-sm mb-2">{item.category}</p>
              
              {/* Stock Status */}
              {isOutOfStock && (
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <span className="text-error text-sm font-body-semibold">Out of Stock</span>
                </div>
              )}
              {isLowStock && !isOutOfStock && (
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning" />
                  <span className="text-warning text-sm">Only {item.stock} left</span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-center space-x-2 mb-3">
                <span className="font-data font-data-medium text-xl text-primary">
                  ${item.price.toFixed(2)}
                </span>
                {item.originalPrice && item.originalPrice > item.price && (
                  <span className="text-text-secondary text-sm line-through">
                    ${item.originalPrice.toFixed(2)}
                  </span>
                )}
                {item.discount && (
                  <span className="bg-accent text-accent-foreground text-xs px-2 py-1 rounded-input">
                    {item.discount}% OFF
                  </span>
                )}
              </div>
            </div>

            {/* Quantity Controls & Actions */}
            <div className="flex flex-col items-end space-y-3">
              {/* Quantity Controls */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handleQuantityChange(item.quantity - 1)}
                  disabled={isOutOfStock || item.quantity <= 1}
                  className="w-8 h-8 p-0"
                  iconName="Minus"
                  iconSize={16}
                />
                <span className="font-data font-data-medium text-lg w-12 text-center">
                  {item.quantity}
                </span>
                <Button
                  variant="outline"
                  onClick={() => handleQuantityChange(item.quantity + 1)}
                  disabled={isOutOfStock || item.quantity >= item.stock}
                  className="w-8 h-8 p-0"
                  iconName="Plus"
                  iconSize={16}
                />
              </div>

              {/* Item Total */}
              <div className="text-right">
                <span className="font-data font-data-medium text-lg text-primary">
                  ${(item.price * item.quantity).toFixed(2)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  onClick={() => onSaveForLater(item.id)}
                  className="text-text-secondary hover:text-primary"
                  iconName="Heart"
                  iconSize={16}
                />
                <Button
                  variant="ghost"
                  onClick={() => onRemove(item.id)}
                  className="text-error hover:text-error"
                  iconName="Trash2"
                  iconSize={16}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;